import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import AddPersonDialog from "@/components/AddPersonDialog";
import EventsUrlPanel from "@/components/EventsUrlPanel";
import RowActions from "@/components/RowActions";
import ConfirmDelete from "@/components/ConfirmDelete";
import BrainTypeField from "@/components/BrainTypeField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  ChevronRight,
  Building2,
  Users,
  Mail,
  UserCircle2,
  Plus,
  BellRing,
} from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", email: "", password: "", brain_type: null, location: "" };

export default function InstituteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [institute, setInstitute] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  // Edit dialog state
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete state
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/institutes/${id}`),
        api.get(`/institutes/${id}/coaches`),
      ]);
      setInstitute(a.data);
      setCoaches(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate("/admin/institutes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      email: c.email,
      password: "",
      brain_type: c.brain_type ?? null,
      location: c.location ?? "",
    });
    setEditError("");
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditError("");
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email, location: form.location };
      if (form.password) payload.password = form.password;
      if (form.brain_type != null) payload.brain_type = form.brain_type;
      await api.put(`/coaches/${editing.id}`, payload);
      toast.success("Coach updated");
      setEditOpen(false);
      load();
    } catch (err) {
      setEditError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/coaches/${confirmTarget.id}`);
      toast.success(
        data?.deleted_clients
          ? `Coach deleted (and ${data.deleted_clients} client${data.deleted_clients === 1 ? "" : "s"})`
          : "Coach deleted"
      );
      setConfirmTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  if (loading || !institute) {
    return (
      <div className="px-8 lg:px-12 py-12">
        <p className="text-sm text-[#8A9188] uppercase tracking-[0.2em] font-bold">Loading…</p>
      </div>
    );
  }

  return (
    <div className="px-8 lg:px-12 py-12 max-w-7xl" data-testid="institute-detail-page">
      <Link
        to="/admin/institutes"
        data-testid="institute-detail-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#8A9188] hover:text-[#1A1D1A] font-bold mb-6"
      >
        <ArrowLeft strokeWidth={1.5} size={14} />
        All Institutes
      </Link>

      <PageHeader
        overline="Institute"
        title={institute.name}
        description={institute.description || "—"}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <Stat icon={Building2} label="Institute" value={institute.name} hint={`Since ${new Date(institute.created_at).toLocaleDateString()}`} />
        <Stat icon={UserCircle2} label="Admin" value={institute.admin_name} hint={institute.admin_email} />
        <Stat icon={Users} label="Coaches" value={coaches.length} hint={institute.admin_brain_type ? `Admin brain type · ${institute.admin_brain_type}` : "Active members"} />
      </div>

      <div className="mb-10">
        <EventsUrlPanel
          institute={institute}
          saveEndpoint={`/institutes/${id}`}
          refreshEndpoint={`/institutes/${id}/events/refresh`}
          onSaved={(updated) => setInstitute(updated)}
        />
      </div>

      <h2 className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold mb-4 flex items-center justify-between">
        <span>Coaches in this institute</span>
        <Button
          onClick={() => setAddOpen(true)}
          data-testid="institute-detail-add-coach-button"
          className="h-10 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white text-sm font-medium"
        >
          <Plus strokeWidth={1.5} size={16} className="mr-1.5" />
          Add Coach
        </Button>
      </h2>

      <AddPersonDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        endpoint={`/institutes/${id}/coaches`}
        kind="coach"
        onCreated={load}
      />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent
          className="bg-white border-[#E5E2DC] sm:max-w-lg"
          data-testid="institute-detail-edit-coach-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold">
              Edit Coach
            </DialogTitle>
            <DialogDescription className="text-[#5C635A]">
              Update the coach's details. Leave password blank to keep it unchanged.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4 mt-2">
            <Field
              label="Full Name"
              testid="institute-detail-edit-coach-name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              required
            />
            <Field
              label="Email"
              type="email"
              testid="institute-detail-edit-coach-email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              required
            />
            <Field
              label="New Password (optional)"
              type="password"
              testid="institute-detail-edit-coach-password"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              placeholder="Leave blank to keep current"
            />
            <BrainTypeField
              value={form.brain_type}
              onChange={(v) => setForm({ ...form, brain_type: v })}
              testid="institute-detail-edit-coach-brain-type"
              label="Brain Type (1–9, optional)"
            />
            <Field
              label="Location (optional)"
              testid="institute-detail-edit-coach-location"
              value={form.location}
              onChange={(v) => setForm({ ...form, location: v })}
              placeholder="City, country — visible to prospects during registration"
            />
            {editError && (
              <p
                className="text-sm text-[#9C6A53] bg-[#9C6A53]/10 border border-[#9C6A53]/20 rounded-lg px-4 py-3"
                data-testid="institute-detail-edit-coach-error"
              >
                {editError}
              </p>
            )}
            <DialogFooter>
              <Button
                type="submit"
                disabled={saving}
                data-testid="institute-detail-edit-coach-submit"
                className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
              >
                {saving ? "Saving..." : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDelete
        open={!!confirmTarget}
        onOpenChange={(v) => !v && setConfirmTarget(null)}
        title="Delete this coach?"
        description={
          confirmTarget
            ? `This will permanently delete "${confirmTarget.name}" and ALL of their clients and their onboarding responses. This action cannot be undone.`
            : ""
        }
        onConfirm={handleDelete}
        loading={deleting}
        testIdPrefix="institute-detail-coach-delete"
      />

      <div className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden" data-testid="institute-detail-coaches">
        <div className="grid grid-cols-12 px-6 py-4 text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold border-b border-[#E5E2DC]">
          <div className="col-span-5">Coach</div>
          <div className="col-span-5">Email</div>
          <div className="col-span-2 text-right">Joined</div>
        </div>
        {coaches.length === 0 ? (
          <div className="px-6 py-16 text-center text-[#5C635A]">No coaches in this institute yet.</div>
        ) : (
          coaches.map((c) => (
            <div
              key={c.id}
              onClick={() => navigate(`/details/coach/${c.id}`)}
              data-testid={`institute-detail-coach-row-${c.id}`}
              className="grid grid-cols-12 px-6 py-5 border-b last:border-0 border-[#E5E2DC] items-center hover:bg-[#F9F8F6] transition-colors cursor-pointer group"
            >
              <div className="col-span-5 font-medium text-[#1A1D1A] flex items-center gap-2 flex-wrap">
                {c.name}
                <ChevronRight strokeWidth={1.5} size={14} className="text-[#B37A5F] opacity-0 group-hover:opacity-100 transition-opacity" />
                {c.pending_requests_count > 0 && (
                  <span
                    data-testid={`institute-detail-coach-pending-${c.id}`}
                    title={`${c.pending_requests_count} pending client request${c.pending_requests_count === 1 ? "" : "s"}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-[#F5EAE2] text-[#B37A5F] border border-[#E5D2C2]"
                  >
                    <BellRing strokeWidth={2} size={10} />
                    {c.pending_requests_count} pending
                  </span>
                )}
              </div>
              <div className="col-span-5 text-sm text-[#5C635A] truncate flex items-center gap-1.5">
                <Mail strokeWidth={1.5} size={12} className="text-[#8A9188]" />
                {c.email}
              </div>
              <div className="col-span-2 text-right text-xs text-[#8A9188] uppercase tracking-wider flex items-center justify-end gap-2">
                <span>{new Date(c.created_at).toLocaleDateString()}</span>
                <RowActions
                  onEdit={() => openEdit(c)}
                  onDelete={() => setConfirmTarget(c)}
                  testIdPrefix={`institute-detail-coach-${c.id}`}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <Icon strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#8A9188] font-bold">{label}</span>
      </div>
      <p className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold truncate">{value}</p>
      {hint && <p className="text-xs text-[#8A9188] mt-2 truncate">{hint}</p>}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">{label}</Label>
      <Input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="h-11 rounded-lg bg-white border-[#E5E2DC] focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
      />
    </div>
  );
}
